import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Grid,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download,
  Refresh,
  CheckCircle,
  Receipt,
  TrendingUp,
  AccountBalance,
  Payment,
  PictureAsPdf
} from '@mui/icons-material';
import {
  getTransactions,
  getPayouts,
  getMonthlyReport,
  calculateMonthlyBilling,
  markPayoutPaid,
  generateMonthlyPayouts,
  generateInvoices,
  exportMonthlyCSV,
  downloadInvoicePDF,
  downloadMonthlyReportPDF,
  type Transaction,
  type Payout,
  type MonthlyReportSummary,
  type BillingCalculation
} from '@/services/api/revenueService';

// タブパネルコンポーネント
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// 金額フォーマット
const formatCurrency = (amount: number, currency = 'JPY') => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// 日付フォーマット
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const RevenuePage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // フィルター
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);

  // データ
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsSummary, setTransactionsSummary] = useState<{
    total_count: number;
    total_gross: number;
    total_net: number;
    total_fee: number;
  } | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutsSummary, setPayoutsSummary] = useState<{
    pending_total: number;
    pending_count: number;
  } | null>(null);
  const [report, setReport] = useState<MonthlyReportSummary | null>(null);
  const [billing, setBilling] = useState<BillingCalculation | null>(null);

  // ダイアログ
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  // データ取得
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await getTransactions({ year, month, limit: 100 });
      setTransactions(data.items);
      setTransactionsSummary(data.summary);
    } catch (err) {
      setError('取引データの取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const data = await getPayouts({ limit: 100 });
      setPayouts(data.items);
      setPayoutsSummary(data.summary);
    } catch (err) {
      setError('支払いデータの取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const data = await getMonthlyReport(year, month);
      setReport(data.summary);
    } catch (err) {
      setError('レポートの取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBilling = async () => {
    try {
      setLoading(true);
      const data = await calculateMonthlyBilling(year, month);
      setBilling(data);
    } catch (err) {
      setError('請求計算に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) fetchTransactions();
    else if (tabValue === 1) fetchPayouts();
    else if (tabValue === 2) fetchReport();
    else if (tabValue === 3) fetchBilling();
  }, [tabValue, year, month]);

  // 支払い完了
  const handleMarkPaid = async (payout: Payout) => {
    try {
      await markPayoutPaid(payout.id);
      setSuccess(`${payout.recipient_name} への支払いを完了にしました`);
      fetchPayouts();
    } catch (err) {
      setError('支払い完了処理に失敗しました');
    }
  };

  // 支払い生成
  const handleGeneratePayouts = async () => {
    try {
      setLoading(true);
      const result = await generateMonthlyPayouts(year, month);
      setSuccess(`${result.created.length}件の支払いを生成しました`);
      setPayoutDialogOpen(false);
      fetchPayouts();
    } catch (err) {
      setError('支払い生成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 請求書生成
  const handleGenerateInvoices = async () => {
    try {
      setLoading(true);
      const result = await generateInvoices(year, month);
      setSuccess(`${result.summary.created_count}件の請求書を生成しました（合計: ${formatCurrency(result.summary.total_amount)}）`);
      setInvoiceDialogOpen(false);
      fetchTransactions();
    } catch (err) {
      setError('請求書生成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // CSVエクスポート
  const handleExportCSV = async (type: 'transactions' | 'distributions' | 'payouts') => {
    try {
      const blob = await exportMonthlyCSV(year, month, type);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue_${type}_${year}_${month.toString().padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('CSVをダウンロードしました');
    } catch (err) {
      setError('CSVエクスポートに失敗しました');
    }
  };

  // PDFダウンロード（請求書）
  const handleDownloadInvoicePDF = async (transactionId: number) => {
    try {
      const blob = await downloadInvoicePDF(transactionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_INV-${transactionId.toString().padStart(6, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('請求書PDFをダウンロードしました');
    } catch (err) {
      setError('請求書PDFのダウンロードに失敗しました');
    }
  };

  // PDFダウンロード（月次レポート）
  const handleDownloadReportPDF = async () => {
    try {
      const blob = await downloadMonthlyReportPDF(year, month);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue_report_${year}_${month.toString().padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      setSuccess('レポートPDFをダウンロードしました');
    } catch (err) {
      setError('レポートPDFのダウンロードに失敗しました');
    }
  };

  // ステータスチップ
  const getStatusChip = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
      refunded: 'info',
      processing: 'info',
      scheduled: 'info'
    };
    const labels: Record<string, string> = {
      completed: '完了',
      pending: '保留',
      failed: '失敗',
      refunded: '返金済',
      processing: '処理中',
      scheduled: '予定'
    };
    return <Chip label={labels[status] || status} color={colors[status] || 'default'} size="small" />;
  };

  // 決済元チップ
  const getSourceChip = (source: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'default'> = {
      stripe: 'primary',
      paypal: 'secondary',
      koneone: 'success',
      platform_fee: 'default',
      bank_transfer: 'default'
    };
    const labels: Record<string, string> = {
      stripe: 'Stripe',
      paypal: 'PayPal',
      koneone: 'コネワン',
      platform_fee: 'プラットフォーム料',
      bank_transfer: '銀行振込'
    };
    return <Chip label={labels[source] || source} color={colors[source] || 'default'} size="small" variant="outlined" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            収益管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            売上・分配・支払いの管理
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            size="small"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ width: 100 }}
          >
            {[2023, 2024, 2025, 2026].map((y) => (
              <MenuItem key={y} value={y}>{y}年</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            sx={{ width: 100 }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m}>{m}月</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp color="primary" />
                <Typography variant="subtitle2" color="text.secondary">総売上</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(transactionsSummary?.total_gross || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Receipt color="success" />
                <Typography variant="subtitle2" color="text.secondary">純収益</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(transactionsSummary?.total_net || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Payment color="warning" />
                <Typography variant="subtitle2" color="text.secondary">手数料</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(transactionsSummary?.total_fee || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountBalance color="info" />
                <Typography variant="subtitle2" color="text.secondary">未払い</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(payoutsSummary?.pending_total || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* タブ */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="取引一覧" />
            <Tab label="支払い管理" />
            <Tab label="月次レポート" />
            <Tab label="請求計算" />
          </Tabs>
        </Box>

        {/* 取引一覧 */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              startIcon={<Download />}
              onClick={() => handleExportCSV('transactions')}
            >
              CSV出力
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日時</TableCell>
                    <TableCell>決済元</TableCell>
                    <TableCell>テナント</TableCell>
                    <TableCell>顧客</TableCell>
                    <TableCell align="right">金額</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{formatDate(tx.transaction_date)}</TableCell>
                      <TableCell>{getSourceChip(tx.payment_source)}</TableCell>
                      <TableCell>{tx.tenant_name || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{tx.customer_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tx.customer_email}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(tx.gross_amount, tx.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(tx.status)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="請求書PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadInvoicePDF(tx.id)}
                          >
                            <PictureAsPdf />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          取引データがありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 支払い管理 */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={() => setPayoutDialogOpen(true)}
            >
              支払い生成
            </Button>
            <Button
              startIcon={<Download />}
              onClick={() => handleExportCSV('payouts')}
            >
              CSV出力
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>受取人</TableCell>
                    <TableCell>期間</TableCell>
                    <TableCell align="right">金額</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>支払日</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payouts.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {payout.recipient_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payout.recipient_email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(payout.net_amount, payout.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(payout.status)}</TableCell>
                      <TableCell>
                        {payout.paid_date ? new Date(payout.paid_date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {payout.status === 'pending' && (
                          <Tooltip title="支払い完了">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleMarkPaid(payout)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {payouts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          支払いデータがありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 月次レポート */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadReportPDF}
              >
                PDF出力
              </Button>
              <Button
                startIcon={<Download />}
                onClick={() => handleExportCSV('distributions')}
              >
                CSV出力
              </Button>
            </Stack>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : report ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>サマリー</Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">取引件数</Typography>
                        <Typography fontWeight="medium">{report.total_transactions}件</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">総売上</Typography>
                        <Typography fontWeight="medium">{formatCurrency(report.total_gross_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">手数料</Typography>
                        <Typography fontWeight="medium">{formatCurrency(report.total_fee_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">純収益</Typography>
                        <Typography fontWeight="bold" color="primary">
                          {formatCurrency(report.total_net_amount)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary">返金額</Typography>
                        <Typography color="error">{formatCurrency(report.total_refunds)}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>決済元別</Typography>
                    <Stack spacing={1}>
                      {Object.entries(report.breakdown_by_source).map(([source, data]) => (
                        <Box key={source} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {getSourceChip(source)}
                          <Typography>
                            {data.count}件 / {formatCurrency(data.amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              {report.breakdown_by_recipient && report.breakdown_by_recipient.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>受取人別</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>受取人</TableCell>
                              <TableCell>メール</TableCell>
                              <TableCell align="right">件数</TableCell>
                              <TableCell align="right">合計</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {report.breakdown_by_recipient.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.email || '-'}</TableCell>
                                <TableCell align="right">{item.count}</TableCell>
                                <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              レポートデータがありません
            </Typography>
          )}
        </TabPanel>

        {/* 請求計算 */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1">
              {year}年{month}月 請求プレビュー
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setInvoiceDialogOpen(true)}
            >
              請求書を生成
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : billing ? (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>テナント</TableCell>
                      <TableCell>プロダクト</TableCell>
                      <TableCell>課金モデル</TableCell>
                      <TableCell align="right">月額</TableCell>
                      <TableCell align="right">ユーザー課金</TableCell>
                      <TableCell align="right">合計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billing.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{item.tenant_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.tenant_code}</Typography>
                        </TableCell>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>
                          <Chip label={item.billing_model} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.breakdown.monthly_fee)}</TableCell>
                        <TableCell align="right">
                          {item.breakdown.billable_users}名 × {formatCurrency(item.breakdown.per_user_fee)}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            = {formatCurrency(item.breakdown.user_fee_total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">{formatCurrency(item.total)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {billing.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            請求対象のデータがありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {billing.items.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Card variant="outlined" sx={{ px: 3, py: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">合計請求額</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(billing.summary.grand_total)}
                    </Typography>
                  </Card>
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              請求データがありません
            </Typography>
          )}
        </TabPanel>
      </Card>

      {/* 支払い生成ダイアログ */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)}>
        <DialogTitle>月次支払い生成</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {year}年{month}月の分配データから支払いを生成します。
          </Typography>
          <Alert severity="info">
            最低支払額（¥5,000）未満の分配は翌月に繰り越されます。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleGeneratePayouts} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : '生成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 請求書生成ダイアログ */}
      <Dialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)}>
        <DialogTitle>請求書生成</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {year}年{month}月の請求書を生成します。
          </Typography>
          <Alert severity="warning">
            既に生成済みの請求はスキップされます。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleGenerateInvoices} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : '生成'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
